import React, { useState } from 'react';
import {
  alpha,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Slide,
  Typography,
  UIButton as Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '../ui';
import { useAuth } from '../shared/state/store';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Select from '@/ui/fields/Select';
import FreeSelect from '@/ui/fields/FreeSelect';
import SpecialTextField from '@/ui/fields/SpecialTextField';
import TextWithTooltip from '@/components/TextWithTooltip';
import CollapsibleTextArea from '@/components/CollapsibleTextArea';
import SelectToggle from '@/components/SelectToggle';
import QuestionGroup from '@/components/QuestionGroup';
import { useReferenceIdHelp } from '@/components/referenceId';
import { useAlert } from '@/app/providers/AlertProvider';
import { UI_MESSAGES } from '@/constants/ui';
import { DebtsService } from '@/shared/api/services/debtsService';
import { SharingOrchestrator } from '@/shared/api/services/sharingOrchestrator';
import {
  mapDebtFrequencyUiToApi,
  mapDebtFrequencyApiToUi,
} from '@/shared/lib/budgetFieldMapping';
import {
  buildLenderLookup,
  computeSelectionContext,
  normalize as normalizeCompanyName,
} from '@/shared/arrears/companyRules';
import type {
  CustomerDebtRecord,
  DebtRepaymentFrequencyUi,
  DebtUpsertRequest,
  PaymentOfferUpsertRequest,
} from '@/shared/api/types';
import { getCustomerId } from '@/shared/lib/utils/getCustomerId';
import { DebtCompaniesService } from '@/shared/api/services/DebtCompaniesService';
import type { LenderParentRecord } from '@/shared/api/services/lenderService';
import {
  HIDDEN_COMPANY_NAMES,
  MANUAL_COMPANY_LABEL,
  MANUAL_COMPANY_OPTION_VALUE,
} from '@/pages/private/noNavButFooter/UpdateBudgetPageCom/constants';

interface ArrearFormItem {
  [key: string]: any;
}

// Removed unused imports: FormProvider and useForm from react-hook-form

interface FieldConfig {
  fieldName: string;
  dataType: string;
  label: string | null;
  [key: string]: any;
}

type CompanyOption = { value: string; label: string };

const coerceCompanyOptions = (input: unknown): CompanyOption[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  return input
    .map((entry) => {
      if (!entry) {
        return null;
      }
      if (typeof entry === 'string') {
        const trimmed = entry.trim();
        return trimmed ? { value: trimmed, label: trimmed } : null;
      }
      if (typeof entry === 'object') {
        const rawValue =
          'value' in entry ? String((entry as { value?: unknown }).value ?? '') : '';
        const rawLabel =
          'label' in entry ? String((entry as { label?: unknown }).label ?? '') : '';
        const resolvedValue = (rawValue || rawLabel).trim();
        if (!resolvedValue) {
          return null;
        }
        return {
          value: resolvedValue,
          label: (rawLabel || rawValue).trim() || resolvedValue,
        };
      }
      return null;
    })
    .filter((option): option is CompanyOption => Boolean(option && option.value));
};

const sanitizeCompanyOptions = (
  options: CompanyOption[],
  hiddenKeys: Set<string>,
): CompanyOption[] => {
  const seen = new Set<string>();
  const sanitized: CompanyOption[] = [];
  options.forEach((option) => {
    const value = option.value.trim();
    const label = (option.label ?? '').trim() || value;
    const key = normalizeCompanyName(value || label);
    if (!key || hiddenKeys.has(key) || seen.has(key)) {
      return;
    }
    seen.add(key);
    sanitized.push({ value, label });
  });
  return sanitized;
};

interface ArrearFormProps {
  label?: string | null;
  showAsCheckBox?: boolean;
  addMore?: boolean;
  formTitle?: string;
  formFields?: FieldConfig[];
  value?: ArrearFormItem[];
  onChange?: (items: ArrearFormItem[]) => void;
  error?: boolean;
  helperText?: string | undefined;
  renderField?: (field: FieldConfig) => React.ReactNode;
  form?: any;
  fieldName?: string;
  // When true, render the built-in arrears dialog (no JSON children needed)
  forceArrearsDialog?: boolean;
  // Optional config to feed options/title without relying on JSON children
  arrearsConfig?: {
    formTitle?: string;
    companies?: Array<{ value: string; label: string }>;
    products?: Array<{ value: string; label: string }>;
    accountTypes?: Array<{ value: string; label: string }>;
    repaymentFrequencyOptions?: Array<{ value: string; label: string }>;
  };
  // Optional explicit accTypeCode for the arrears debt, provided from JSON (preferred over inference)
  arrearsAccTypeCode?: string;
  // Optional link mapping from question-group to arrears dialog fields
  linkComponentToArrearsProps?: {
    amount?: string;
    frequency?: string;
    notes?: string;
    repaymentAmount?: string;
    repaymentFrequency?: string;
    repaymentNotes?: string;
    companyName?: string;
    companyOtherName?: string;
  };
  /**
   * Optional list of existing creditor/company names from the Debts API.
   * When provided, selecting the same company will surface a warning.
   * If not provided, ArrearForm will fetch debts on first dialog open.
   */
  existingCreditorNames?: string[];
}

// Transition component for mobile slide-up
const Transition = React.forwardRef<unknown, { children: React.ReactElement }>(
  function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  }
);

const ArrearForm: React.FC<ArrearFormProps> = ({
  label,
  showAsCheckBox = false,
  formTitle = 'Add Item',
  formFields = [],
  value = [],
  onChange,
  error = false,
  helperText,
  renderField,
  form,
  fieldName,
  forceArrearsDialog = false,
  arrearsConfig,
  linkComponentToArrearsProps,
  arrearsAccTypeCode,
  existingCreditorNames,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { showAlert } = useAlert();

  const [isChecked, setIsChecked] = useState<boolean>(() => {
    try {
      const fieldPath = String(fieldName || '');
      if (form && typeof form.getValues === 'function' && fieldPath.includes('.')) {
        const parts = fieldPath.split('.');
        parts[parts.length - 1] = 'inArrears';
        const inArrearsPath = parts.join('.');
        const v = form.getValues(inArrearsPath);
        if (typeof v === 'boolean') return v;
      }
    } catch {
      // ignore and fall back to items length
    }
    return Array.isArray(value) && value.length > 0;
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const wasSavedRef = React.useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  // Prevent immediate auto-reopen after a save updates the list length
  const suppressNextAutoOpenRef = React.useRef(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Warning message when preventing untick while an arrear exists
  const [untickWarning, setUntickWarning] = useState<string | null>(null);
  // Track attempted submit to surface validation errors
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  // Track "I don't have any Reference ID" checkbox for arrears reference number
  const [noReferenceId, setNoReferenceId] = useState(false);
  // Hidden Freepost notes used only for sharing payloads
  const [freepostNotes, setFreepostNotes] = useState<string>('');
  const lastApostropheAlertAtRef = React.useRef<number>(0);
  const lastDuplicateCompanyAlertAtRef = React.useRef<number>(0);
  const [existingCreditorNamesFromApi] = useState<string[]>([]);
  // Track when user explicitly clears repayment amount so we can persist 0 instead of null.
  const repaymentClearedRef = React.useRef(false);
  // Track when user explicitly clears payment offer amount so we can delete the offer record.
  const paymentOfferClearedRef = React.useRef(false);

  // Use a unique prefix for temporary form fields to avoid conflicts with parent form
  const tempFieldPrefix = `__ArrearForm_temp_${fieldName || 'default'}__`;

  // Arrears specific state (used when this list is an arrears list)
  const isArrearsList =
    forceArrearsDialog ||
    (typeof fieldName === 'string' && fieldName.endsWith('.arrears'));
  type ArrearsDraft = {
    companyName: string;
    companyNameManual: string;
    productName: string;
    accountType: string;
    referenceNumber: string;
    amountCurrentlyOwned: string;
    repaymentAmount: string;
    frequency: string;
    notes: string;
    paymentOfferAmount: string;
    paymentOfferMode: string;
  };
  const createInitialArrearsDraft = React.useCallback<() => ArrearsDraft>(
    () => ({
      companyName: '',
      companyNameManual: '',
      productName: '',
      accountType: '',
      referenceNumber: '',
      amountCurrentlyOwned: '',
      repaymentAmount: '',
      frequency: 'monthly',
      notes: '',
      paymentOfferAmount: '',
      paymentOfferMode: '',
    }),
    [],
  );
  const [arrearsDraft, setArrearsDraft] = useState<ArrearsDraft>(() => createInitialArrearsDraft());

  // Build select options from provided schema child field definitions
  const findChildField = (childName: string): FieldConfig | undefined =>
    Array.isArray(formFields)
      ? formFields.find((f) => f.fieldName === childName)
      : undefined;
  const companyFieldDef = findChildField('companyName');
  const productFieldDef = findChildField('productName');
  const accountTypeFieldDef = findChildField('accountType');

  const productOptions =
    arrearsConfig?.products ??
    ((Array.isArray(productFieldDef?.options)
      ? (productFieldDef?.options as Array<{ value: string; label: string }>)
      : []) || []);
  const accountTypeOptions =
    arrearsConfig?.accountTypes ??
    ((Array.isArray(accountTypeFieldDef?.options)
      ? (accountTypeFieldDef?.options as Array<{ value: string; label: string }>)
      : []) || []);
  const [fallbackCompanyOptions, setFallbackCompanyOptions] = useState<CompanyOption[]>([]);
  const hiddenCompanyKeys = React.useMemo(
    () =>
      new Set(
        HIDDEN_COMPANY_NAMES.map((name) => normalizeCompanyName(name)),
      ),
    [],
  );
  const schemaCompanyOptions = React.useMemo(() => {
    const source =
      arrearsConfig?.companies ??
      (Array.isArray(companyFieldDef?.options) ? companyFieldDef?.options : []);
    return sanitizeCompanyOptions(coerceCompanyOptions(source), hiddenCompanyKeys);
  }, [arrearsConfig, companyFieldDef, hiddenCompanyKeys]);

  // Prefill hidden Freepost covering notes (used only when a Freepost creditor is selected)
  React.useEffect(() => {
    try {
      const customerDataStr = localStorage.getItem('customerData');
      if (!customerDataStr) return;
      const customerData = JSON.parse(customerDataStr);
      if (customerData?.mobileNumber) {
        setFreepostNotes(
          `I am in financial difficulty and because of this I have used IE Hub to review my budget.  I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you. Please call me on ${customerData.mobileNumber} so we can discuss my options.`,
        );
      } else {
        setFreepostNotes(
          "I am in financial difficulty and because of this I have used IE Hub to review my budget.  I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you.",
        );
      }
    } catch {
      // ignore parse errors, fall back to empty notes
    }
  }, []);

  // Some schemas only provide the manual "Other / I can't find my company" option.
  // When that happens, we should still fall back to the full lender list so that
  // known creditors (e.g. Anglian Water) appear in the dropdown.
  const hasRealSchemaCompanies = React.useMemo(() => {
    if (schemaCompanyOptions.length === 0) return false;
    const isManualLike = (opt: CompanyOption): boolean => {
      const valueNorm = normalizeCompanyName(opt.value);
      const labelNorm = normalizeCompanyName(opt.label);
      if (!valueNorm && !labelNorm) return true;
      if (
        valueNorm === 'other' ||
        valueNorm === '__manual__' ||
        valueNorm === '__manual_company__'
      ) {
        return true;
      }
      if (labelNorm.includes("can't find my")) {
        return true;
      }
      return false;
    };
    // If every option looks like a manual/sentinel entry, then there are no real companies.
    const allManual = schemaCompanyOptions.every(isManualLike);
    return !allManual;
  }, [schemaCompanyOptions]);

  const baseCompanyOptions =
    hasRealSchemaCompanies && schemaCompanyOptions.length > 0
      ? schemaCompanyOptions
      : fallbackCompanyOptions;
  const [lenders, setLenders] = useState<LenderParentRecord[]>([]);
  const [lendersLoaded, setLendersLoaded] = useState(false);

  // When syncing "manual" suppliers back to the parent select field (e.g. gasSupplier),
  // prefer to use the actual option value for the "I can't find my company" style entry
  // when present, otherwise fall back to the classic "Other" token. This keeps the
  // select visually in sync with the text box that captures the freepost/manual name.
  const resolveOtherSelectValue = React.useCallback((): string => {
    // For utilities supplier dropdowns (`gasSupplier` / `electricitySupplier`),
    // the canonical value that triggers the "Other supplier" textbox is "Other"
    // (with label "I can't find my company"). Some arrears dialogs use a
    // different internal sentinel (e.g. "__manual__"), but when syncing back
    // to the main supplier field we always want to write "Other" so that:
    //   - the Supplier select visibly shows "I can't find my company"
    //   - existing schema `visibleWhen` rules (which expect "Other") continue to work. 
    return 'Other';
  }, []);

  const manualCompanyOption = React.useMemo<CompanyOption>(
    () => ({
      value: resolveOtherSelectValue(),
      label: MANUAL_COMPANY_LABEL,
    }),
    [resolveOtherSelectValue],
  );

  const childCompanyKeys = React.useMemo(() => {
    const keys = new Set<string>();
    lenders.forEach((parent) => {
      const children = Array.isArray(parent.childCompany) ? parent.childCompany : [];
      children.forEach((child) => {
        const key = normalizeCompanyName(child);
        if (key) {
          keys.add(key);
        }
      });
    });
    return keys;
  }, [lenders]);

  const companyOptionsForSelect = React.useMemo(() => {
    const normalizedManual = normalizeCompanyName(manualCompanyOption.value);
    const base = [...baseCompanyOptions];
    const hasManual = base.some(
      (opt) => normalizeCompanyName(opt.value) === normalizedManual,
    );
    const withManual = hasManual ? base : [manualCompanyOption, ...base];
    const selectedValue = String(arrearsDraft.companyName || '').trim();
    const normalizedSelection = normalizeCompanyName(selectedValue);
    const filtered = withManual.filter((option) => {
      const valueKey = normalizeCompanyName(option.value);
      const labelKey = normalizeCompanyName(option.label);
      const matchesSelection =
        normalizedSelection && (valueKey === normalizedSelection || labelKey === normalizedSelection);
      if (matchesSelection) {
        return true;
      }
      const isChild = childCompanyKeys.has(valueKey) || childCompanyKeys.has(labelKey);
      return !isChild;
    });
    if (!selectedValue || filtered.some(
      (opt) => normalizeCompanyName(opt.value) === normalizedSelection,
    )) {
      return filtered;
    }
    return [
      {
        value: selectedValue,
        label: (arrearsDraft.companyNameManual || selectedValue).trim() || selectedValue,
      },
      ...filtered,
    ];
  }, [
    arrearsDraft.companyName,
    arrearsDraft.companyNameManual,
    baseCompanyOptions,
    childCompanyKeys,
    manualCompanyOption,
  ]);

  const lenderLookup = React.useMemo(() => buildLenderLookup(lenders), [lenders]);
  const normalizedCompanySelection = React.useMemo(
    () => normalizeCompanyName(arrearsDraft.companyName),
    [arrearsDraft.companyName],
  );

  const matchedCompanyOption = React.useMemo(() => {
    if (!normalizedCompanySelection) {
      return undefined;
    }
    return companyOptionsForSelect.find((option) => {
      const valKey = normalizeCompanyName(option.value);
      const labelKey = normalizeCompanyName(option.label);
      return valKey === normalizedCompanySelection || labelKey === normalizedCompanySelection;
    });
  }, [companyOptionsForSelect, normalizedCompanySelection]);

  const existingCreditorNameSet = React.useMemo(() => {
    const names = Array.isArray(existingCreditorNames) && existingCreditorNames.length > 0
      ? existingCreditorNames
      : existingCreditorNamesFromApi;
    const set = new Set<string>();
    if (Array.isArray(names)) {
      names.forEach((name) => {
        const key = normalizeCompanyName(name);
        if (key) {
          set.add(key);
        }
      });
    }
    return set;
  }, [existingCreditorNames, existingCreditorNamesFromApi]);

  const manualSelectionForContext = React.useMemo(() => {
    if (!normalizedCompanySelection) {
      return false;
    }
    const manualKeys = [
      normalizeCompanyName(resolveOtherSelectValue()),
      normalizeCompanyName(MANUAL_COMPANY_OPTION_VALUE),
      '__manual__',
      '__manual_company__',
    ];
    return manualKeys.includes(normalizedCompanySelection);
  }, [normalizedCompanySelection, resolveOtherSelectValue]);

  const companySelectionForContext = React.useMemo(() => {
    if (!arrearsDraft.companyName) {
      return '';
    }
    if (manualSelectionForContext) {
      return arrearsDraft.companyName;
    }
    return matchedCompanyOption?.label ?? arrearsDraft.companyName;
  }, [arrearsDraft.companyName, manualSelectionForContext, matchedCompanyOption]);

  const arrearsSelectionContext = React.useMemo(
    () =>
      computeSelectionContext({
        companySelection: companySelectionForContext,
        productSelection: String(arrearsDraft.productName || ''),
        manualCompanyName: arrearsDraft.companyNameManual,
        lenders,
        accountTypes: [],
        lookup: lenderLookup,
        manual: manualSelectionForContext,
      }),
    [
      companySelectionForContext,
      arrearsDraft.productName,
      arrearsDraft.companyNameManual,
      lenders,
      lenderLookup,
      manualSelectionForContext,
    ],
  );

  // Helper: detect whether a selected option represents "Other"/"I can't find my company"
  const isOtherValue = React.useCallback(
    (val: string | undefined | null): boolean => {
      const normalized = normalizeCompanyName(val ?? '');
      if (!normalized) return false;
      if (
        normalized === normalizeCompanyName(resolveOtherSelectValue()) ||
        normalized === normalizeCompanyName(MANUAL_COMPANY_OPTION_VALUE) ||
        normalized === '__manual__' ||
        normalized === '__manual_company__'
      ) {
        return true;
      }
      const opt = companyOptionsForSelect.find((option) => {
        const valKey = normalizeCompanyName(option.value);
        const labelKey = normalizeCompanyName(option.label);
        return valKey === normalized || labelKey === normalized;
      });
      if (!opt) return false;
      const labelKey = normalizeCompanyName(opt.label);
      return (
        labelKey.includes("can't find my referred") ||
        labelKey.includes("can't find my") ||
        labelKey === 'other'
      );
    },
    [companyOptionsForSelect, resolveOtherSelectValue],
  );

  const REPAYMENT_OPTIONS: { value: string; label: string }[] = [
    { value: 'weekly', label: 'Weekly' },
    { value: '4weekly', label: '4-Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  // Linked fields support - build full form path for a mapped key
  type LinkPropsMap = {
    amount?: string;
    frequency?: string;
    notes?: string;
    repaymentAmount?: string;
    repaymentFrequency?: string;
    repaymentNotes?: string;
    companyName?: string;
    companyOtherName?: string;
  };
  const resolveLinkedPath = React.useCallback(
    (key: keyof LinkPropsMap): string | null => {
      const map = (linkComponentToArrearsProps || {}) as LinkPropsMap;
      const target = map[key];
      if (!target) return null;
      const fp = String(fieldName || '');
      const parentPath = fp.includes('.') ? fp.split('.').slice(0, -1).join('.') : '';

      // For supplier fields we always treat the mapped key as a child of the
      // parent question-group (e.g. "gas.gasSupplier", "gas.gasSupplierOther").
      // These fields are never root-level in our schemas, and relying on
      // current form values to detect them can fail when the group has not
      // yet been populated. This was causing us to incorrectly write to a
      // root-level "gasSupplier" field, leaving the real Supplier dropdown
      // at "Select an option".
      if (
        parentPath &&
        (key === 'companyName' || key === 'companyOtherName') &&
        !target.includes('.')
      ) {
        return `${parentPath}.${target}`;
      }

      // If target is dotted, treat as absolute form path
      if (target.includes('.')) return target;
      // Try to detect if the target is a child key of the parent question-group; if yes, prefix parent path
      try {
        if (form && parentPath && typeof form.getValues === 'function') {
          const groupData = form.getValues(parentPath) as Record<string, unknown> | undefined;
          if (groupData && Object.prototype.hasOwnProperty.call(groupData, target)) {
            return `${parentPath}.${target}`;
          }
        }
      } catch {
        // swallow lookup errors and fall through
      }
      // Otherwise treat as absolute root-level field (e.g., gasBill, gasBillMode)
      return target;
    },
    [linkComponentToArrearsProps, fieldName]
  );
  // Resolve the first available mapped path among provided keys
  const resolveFirstLinkedPath = React.useCallback(
    (keys: (keyof LinkPropsMap)[]): string | null => {
      for (const k of keys) {
        const p = resolveLinkedPath(k);
        if (p) return p;
      }
      return null;
    },
    [resolveLinkedPath]
  );

  const triggerBlockedCharacterAlert = React.useCallback(
    (isOverLimit?: boolean) => {
      const now = Date.now();
      if (
        lastApostropheAlertAtRef.current &&
        now - lastApostropheAlertAtRef.current < 1500
      ) {
        return;
      }
      lastApostropheAlertAtRef.current = now;
      const message = isOverLimit
        ? UI_MESSAGES.CHARACTER_LIMIT_REACHED
        : UI_MESSAGES.APOSTROPHE_NOT_ALLOWED;

      try {
        window.dispatchEvent(
          new CustomEvent('iehub-alert', {
            detail: {
              severity: 'warning',
              message: message,
              variant: 'filled',
              duration: 3000,
            },
          }),
        );
      } catch {
        // no-op
      }
    },
    [],
  );

  const handleReferenceValueChange = React.useCallback(
    (raw: string) => {
      const nextValue = String(raw ?? '');
      const isOverLimit = nextValue.length > 50;
      const hasApostrophe = nextValue.includes("'");

      if (isOverLimit || hasApostrophe) {
        triggerBlockedCharacterAlert(isOverLimit);
      }

      const sanitized = nextValue.replace(/'/g, '').slice(0, 50);
      setArrearsDraft((prev) => ({
        ...prev,
        referenceNumber: sanitized,
      }));
      if (sanitized.trim().length > 0 && noReferenceId) {
        setNoReferenceId(false);
      }
    },
    [noReferenceId, triggerBlockedCharacterAlert],
  );

  // Validation: company is mandatory (and manual name when "Other")
  const companyRequiresManual =
    isOtherValue(arrearsDraft.companyName) || arrearsDraft.companyName === '__manual__';
  const isCompanySelected = String(arrearsDraft.companyName || '').trim() !== '';
  const isManualProvided =
    !companyRequiresManual || String(arrearsDraft.companyNameManual || '').trim() !== '';
  const isCompanyValid = isCompanySelected && isManualProvided;
  const resolvedCompanyNameForDisplay = React.useMemo(() => {
    if (companyRequiresManual) {
      return String(arrearsDraft.companyNameManual || '').trim();
    }
    if (!arrearsDraft.companyName) {
      return '';
    }
    return matchedCompanyOption?.label ?? arrearsDraft.companyName;
  }, [arrearsDraft.companyName, arrearsDraft.companyNameManual, companyRequiresManual, matchedCompanyOption]);

  const selectionProductOptions = React.useMemo(
    () =>
      arrearsSelectionContext.showProduct
        ? arrearsSelectionContext.productOptions
        : [],
    [arrearsSelectionContext.productOptions, arrearsSelectionContext.showProduct],
  );
  const {
    helpDialog: arrearsReferenceHelpDialog,
    helpLinkText: arrearsReferenceHelpLinkText,
    noValueCheckboxLabel: arrearsNoValueCheckboxLabel,
    label: arrearsReferenceLabel,
  } = useReferenceIdHelp({
    // Mirror Debts behaviour:
    // - prefer manual company name when using "Other"
    // - otherwise prefer product name (when present)
    // - otherwise fall back to the selected/display company
    companyName: (() => {
      const manual = String(arrearsDraft.companyNameManual || '').trim();
      if (companyRequiresManual && manual) return manual;
      const product = String(arrearsDraft.productName || '').trim();
      if (product) return product;
      return (
        (companyRequiresManual ? manual : resolvedCompanyNameForDisplay) ||
        String(arrearsDraft.companyName || '').trim()
      );
    })(),
    // Allow reference-id help to vary by product schema (same as DebtCategory)
    backendName: arrearsSelectionContext.effectiveLenderName ?? undefined,
  });

  const configuredProductOptions = React.useMemo(
    () => (Array.isArray(productOptions) ? productOptions : []),
    [productOptions],
  );
  const shouldShowProductSelect = React.useMemo(() => {
    if (arrearsSelectionContext.showProduct) {
      return true;
    }
    return configuredProductOptions.length > 0;
  }, [arrearsSelectionContext.showProduct, configuredProductOptions]);
  const effectiveProductOptions =
    selectionProductOptions.length > 0 ? selectionProductOptions : configuredProductOptions;
  const isProductRequired =
    arrearsSelectionContext.showProduct && arrearsSelectionContext.productRequired;
  const productSelectError =
    attemptedSubmit &&
    isProductRequired &&
    String(arrearsDraft.productName || '').trim().length === 0;

  // Map parent field to arrears accTypeCode (used for utilities and common arrears).
  // Prefer explicit JSON-provided accTypeCode when available; fall back to inferred mapping for legacy schemas.
  const resolveAccTypeCode = React.useCallback(
    (fieldPath?: string | null): string | null => {
      if (arrearsAccTypeCode && arrearsAccTypeCode.trim()) {
        return arrearsAccTypeCode.trim();
      }
      const p = String(fieldPath || '');
      const parentKey = p.includes('.') ? p.split('.').slice(0, -1).pop() : p;
      switch (parentKey) {
        // Gas variants
        case 'gas':
        case 'gas_bill':
          return 'GA';
        // Electricity variants
        case 'electricity':
        case 'electricity_bill':
          return 'EE';
        // Water supply variants
        case 'water':
        case 'waterSupply':
        case 'water_bill':
          return 'WS';
        // Water waste variants
        case 'waterWaste':
        case 'water_waste':
          return 'WA';
        // House rent variants
        case 'houseRent':
        case 'HOUSE_RENT':
          return 'HR';
        // Mortgage
        case 'mortgage':
        case 'MORTGAGE':
          return 'MG';
        // Secured loans variants
        case 'securedLoans':
        case 'other_secured_loans':
          return 'SE';
        // Council tax variants
        // According to the canonical debt type list, "Council Tax Arrears" uses code "CT"
        case 'councilTax':
        case 'COUNCIL_TAX':
          return 'CT';
        default:
          return null;
      }
    },
    [arrearsAccTypeCode]
  );

  // Helpers for numeric parsing & monthly conversion (mirrors Debts screen behaviour)
  const parseAmount = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const raw =
      typeof value === 'number'
        ? value.toString()
        : String(value).replace(/,/g, '');
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const toMonthly = (
    amount: number | null | undefined,
    freq: DebtRepaymentFrequencyUi | null | undefined,
  ): number | null => {
    if (amount === null || amount === undefined) return null;
    if (!Number.isFinite(amount)) return null;
    switch (freq) {
      case 'weekly':
        return ((amount / 7) * 365) / 12;
      case '4weekly':
        return amount * (13 / 12);
      case 'monthly':
        return amount;
      case 'quarterly':
        return amount / 3;
      case 'yearly':
        return amount / 12;
      default:
        return amount;
    }
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '£0.00';
    }
    return `£${Number(value).toFixed(2)}`;
  };

  // Fallback: when parent doesn't provide supplier dropdown and no JSON options
  // (or only the manual "Other" option), load a full lender list (excluding gas/electric categories).
  React.useEffect(() => {
    if (!isArrearsList) return;
    // If the schema already provides real company options, do not fetch a global list.
    if (hasRealSchemaCompanies) return;
    const accCode = resolveAccTypeCode(fieldName);
    if (accCode === 'GA' || accCode === 'EE') return;
    let ignore = false;
    (async () => {
      try {
        const resp = await DebtCompaniesService.fetchCompanies({ skipErrorCapture: true });
        const list: any[] = Array.isArray((resp as any)?.data)
          ? ((resp as any).data as any[])
          : (Array.isArray(resp) ? (resp as any[]) : []);
        const names = new Set<string>();
        for (const rec of list) {
          // Only include companies where addToDebtList is true/Y
          // Check both boolean and string 'Y' formats for addToDebtList
          const addToDebtListValue = rec?.addToDebtList;
          const isAllowedForDebtList =
            addToDebtListValue === true ||
            addToDebtListValue === 'Y' ||
            (rec?.raw as any)?.addToDebtList === 'Y';
          if (!isAllowedForDebtList) {
            continue;
          }
          const parent = String(rec?.parentCompany ?? '').trim();
          if (parent) names.add(parent);
          const children: string[] = Array.isArray(rec?.childCompany) ? rec.childCompany : [];
          for (const child of children) {
            const c = String(child ?? '').trim();
            if (c) names.add(c);
          }
        }
        const mapped = Array.from(names)
          .map((n) => ({ value: n, label: n }))
          .filter((opt) => {
            const val = opt.value.trim().toLowerCase();
            const lbl = opt.label.trim().toLowerCase();
            return val !== 'other' && lbl !== 'other';
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        const manualTopOption: CompanyOption = {
          value: resolveOtherSelectValue(),
          label: MANUAL_COMPANY_LABEL,
        };
        const finalOpts = sanitizeCompanyOptions(
          [manualTopOption, ...mapped],
          hiddenCompanyKeys,
        );
        if (!ignore) setFallbackCompanyOptions(finalOpts);
      } catch {
        // ignore fetch errors; keep empty fallback
      }
    })();
    return () => {
      ignore = true;
    };
  }, [isArrearsList, hasRealSchemaCompanies, fieldName, hiddenCompanyKeys, resolveOtherSelectValue]);

  // Load lender metadata once for arrears lists so we can detect payment-offer enabled companies
  React.useEffect(() => {
    if (!isArrearsList || lendersLoaded) return;
    let ignore = false;
    (async () => {
      try {
        const resp = await DebtCompaniesService.fetchCompanies({ skipErrorCapture: true });
        const data = Array.isArray(resp?.data) ? (resp.data as LenderParentRecord[]) : [];
        if (!ignore) {
          setLenders(data);
          setLendersLoaded(true);
        }
      } catch {
        if (!ignore) {
          setLendersLoaded(true);
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [isArrearsList, lendersLoaded]);

  React.useEffect(() => {
    if (!arrearsSelectionContext.showProduct) {
      return;
    }
    const resolvedProduct = arrearsSelectionContext.resolvedProductSelection;
    setArrearsDraft((prev) =>
      prev.productName === resolvedProduct ? prev : { ...prev, productName: resolvedProduct },
    );
  }, [
    arrearsSelectionContext.showProduct,
    arrearsSelectionContext.resolvedProductSelection,
  ]);

  // Available budget (pulled from root form `balanceAmount`, same as Debts screen)
  const availableBudget = React.useMemo(() => {
    try {
      if (!form || typeof form.getValues !== 'function') return undefined;
      const allValues = form.getValues() as Record<string, unknown>;
      const raw = (allValues?.balanceAmount as unknown) ?? undefined;
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'string') {
        const cleaned = raw.replace(/,/g, '');
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? n : undefined;
      }
    } catch {
      // ignore parse errors
    }
    return undefined;
  }, [form]);

  // Detect if selected company supports payment offer (based on lender config)
  const lenderSupportsPaymentOffer = React.useMemo(() => {
    if (!isArrearsList) return false;
    if (!arrearsDraft.companyName || lenders.length === 0) return false;
    if (isOtherValue(arrearsDraft.companyName)) return false;

    const allOpts = companyOptionsForSelect;
    const selectedOpt = allOpts.find(
      (opt) => normalizeCompanyName(opt.value) === normalizeCompanyName(arrearsDraft.companyName),
    );

    const candidateNames = [arrearsDraft.companyName, selectedOpt?.label ?? '']
      .map((name) => normalizeCompanyName(name))
      .filter((v) => v.length > 0);

    if (candidateNames.length === 0) return false;

    return lenders.some((lender) => {
      if (!lender.customerPaymentOffer && lender.raw?.customerPaymentOffer !== 'Y') {
        return false;
      }
      const lenderNames = [
        lender.parentCompany,
        lender.raw?.lender,
        // Some APIs echo parentCompany on raw; include it defensively
        (lender.raw as any)?.parentCompany,
      ]
        .filter((n) => typeof n === 'string' && n.trim().length > 0)
        .map((n) => normalizeCompanyName(n));
      return lenderNames.some((name) => candidateNames.includes(name));
    });
  }, [
    isArrearsList,
    arrearsDraft.companyName,
    lenders,
    companyOptionsForSelect,
    isOtherValue,
  ]);

  const offerAmountNumeric = React.useMemo(
    () => parseAmount(arrearsDraft.paymentOfferAmount),
    [arrearsDraft.paymentOfferAmount],
  );

  const repaymentAmountNumeric = React.useMemo(
    () => parseAmount(arrearsDraft.repaymentAmount),
    [arrearsDraft.repaymentAmount],
  );

  const repaymentMonthlyForBudget = React.useMemo(() => {
    const uiFrequency = (arrearsDraft.frequency || null) as DebtRepaymentFrequencyUi | null;
    return toMonthly(repaymentAmountNumeric, uiFrequency) ?? 0;
  }, [arrearsDraft.frequency, repaymentAmountNumeric]);

  const coerceNumericIdForBudget = React.useCallback((input: unknown): number | null => {
    if (typeof input === 'number' && Number.isFinite(input) && input > 0) {
      return input;
    }
    if (typeof input === 'string') {
      const trimmed = input.trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return null;
  }, []);

  const existingPaymentOfferId = React.useMemo(() => {
    if (editingIndex === null) return null;
    const item = value?.[editingIndex] as any;
    const id = item?.paymentOfferId;
    return typeof id === 'number' && id > 0 ? id : null;
  }, [editingIndex, value]);

  const existingOfferPresent = React.useMemo(() => {
    const amtOk =
      offerAmountNumeric !== null &&
      typeof offerAmountNumeric === 'number' &&
      !Number.isNaN(offerAmountNumeric) &&
      offerAmountNumeric > 0;
    return amtOk || typeof existingPaymentOfferId === 'number';
  }, [existingPaymentOfferId, offerAmountNumeric]);

  // Baseline values captured from the API-backed item we are editing.
  const originalRepaymentMonthlyForBudget = React.useMemo(() => {
    if (editingIndex === null) {
      return 0;
    }
    const originalItem = value?.[editingIndex] as any;
    const serverId = coerceNumericIdForBudget(originalItem?.id);
    if (!serverId) {
      return 0;
    }
    const originalRepayment = parseAmount(
      originalItem?.repaymentAmount ??
        originalItem?.repayment ??
        (typeof originalItem?.amountCurrentlyOwned === 'object'
          ? originalItem?.amountCurrentlyOwned?.repayment
          : undefined),
    );
    const originalFreqAny =
      originalItem?.frequency ??
      originalItem?.repaymentFrequency ??
      (typeof originalItem?.amountCurrentlyOwned === 'object'
        ? originalItem?.amountCurrentlyOwned?.frequency
        : undefined) ??
      null;
    const mappedUiFreq = mapDebtFrequencyApiToUi(
      typeof originalFreqAny === 'string' ? originalFreqAny : null,
    );
    const rawFreq = String(mappedUiFreq ?? originalFreqAny ?? '')
      .trim()
      .toLowerCase();
    const normalizedFreq =
      rawFreq === 'weekly'
        ? 'weekly'
        : rawFreq === '4weekly' ||
            rawFreq === 'fourweekly' ||
            rawFreq === 'four-weekly'
          ? '4weekly'
          : rawFreq === 'monthly'
            ? 'monthly'
            : rawFreq === 'quarterly'
              ? 'quarterly'
              : rawFreq === 'yearly' || rawFreq === 'annually'
                ? 'yearly'
                : null;
    return toMonthly(originalRepayment, normalizedFreq as DebtRepaymentFrequencyUi | null) ?? 0;
  }, [coerceNumericIdForBudget, editingIndex, value]);

  // Keep a stable baseline offer amount so we can avoid double-subtracting while editing.
  const originalOfferAmountForBudgetRef = React.useRef<number>(0);
  const originalOfferAmountSetRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    // Reset baseline whenever we enter/leave edit mode
    if (editingIndex === null) {
      originalOfferAmountForBudgetRef.current = 0;
      originalOfferAmountSetRef.current = false;
      return;
    }
    const originalItem = value?.[editingIndex] as any;
    const serverId = coerceNumericIdForBudget(originalItem?.id);
    if (!serverId) {
      originalOfferAmountForBudgetRef.current = 0;
      originalOfferAmountSetRef.current = false;
      return;
    }
    const fromItem = parseAmount(
      originalItem?.paymentOfferAmount ??
        originalItem?.pymtOfferAmount ??
        null,
    );
    if (fromItem !== null && Number.isFinite(fromItem)) {
      originalOfferAmountForBudgetRef.current = fromItem;
      originalOfferAmountSetRef.current = true;
    } else {
      originalOfferAmountForBudgetRef.current = 0;
      originalOfferAmountSetRef.current = false;
    }
  }, [coerceNumericIdForBudget, editingIndex, value]);

  const showPaymentOfferPanel = React.useMemo(() => {
    if (typeof availableBudget !== 'number' || Number.isNaN(availableBudget)) {
      return false;
    }
    // If what's left over is <= 0, only show payment offer if one already exists
    // (so user can reduce/clear it). Otherwise it's hidden.
    if (availableBudget <= 0) {
      return existingOfferPresent;
    }
    return lenderSupportsPaymentOffer || existingOfferPresent;
  }, [lenderSupportsPaymentOffer, availableBudget, existingOfferPresent]);

  const effectiveAvailableBudget = React.useMemo(() => {
    if (typeof availableBudget !== 'number' || Number.isNaN(availableBudget)) {
      return null;
    }

    const currentOffer = typeof offerAmountNumeric === 'number' && !Number.isNaN(offerAmountNumeric)
      ? offerAmountNumeric
      : 0;

    // IMPORTANT:
    // `availableBudget` (balanceAmount) already includes the *saved* repayment + saved payment offer.
    // When editing an existing server-backed arrear, compute a baseline that adds those saved values back
    // before subtracting the current (possibly edited) values. This avoids double-subtracting.
    const originalItem = editingIndex !== null ? (value?.[editingIndex] as any) : null;
    const isEditingExisting =
      editingIndex !== null && coerceNumericIdForBudget(originalItem?.id) !== null;
    const originalOffer =
      isEditingExisting && originalOfferAmountSetRef.current
        ? originalOfferAmountForBudgetRef.current
        : 0;
    const baseline =
      availableBudget +
      (isEditingExisting ? originalRepaymentMonthlyForBudget : 0) +
      (isEditingExisting ? originalOffer : 0);

    return baseline - repaymentMonthlyForBudget - currentOffer;
  }, [
    availableBudget,
    coerceNumericIdForBudget,
    editingIndex,
    offerAmountNumeric,
    originalRepaymentMonthlyForBudget,
    repaymentMonthlyForBudget,
    value,
  ]);

  const paymentOfferExceedsBudget = React.useMemo(() => {
    if (
      offerAmountNumeric === null ||
      Number.isNaN(offerAmountNumeric) ||
      offerAmountNumeric <= 0
    ) {
      return false;
    }
    if (typeof effectiveAvailableBudget === 'number') {
      return effectiveAvailableBudget < 0;
    }
    return false;
  }, [offerAmountNumeric, effectiveAvailableBudget]);

  const paymentOfferErrorMessage = paymentOfferExceedsBudget ? 'Offer exceeds amount available' : null;

  // NOTE: We intentionally do NOT sync arrears company selection back to the
  // main supplier fields (e.g. gasSupplier/electricitySupplier). The data flow
  // is one-way: main field → arrears dialog only.

  const isEmailFallbackReference = (raw: string | null | undefined): boolean => {
    const value = (raw ?? '').trim();
    if (!value) {
      return false;
    }
    const email =
      typeof user?.emailAddress === 'string' ? user.emailAddress.trim() : '';
    return Boolean(email && value.toLowerCase() === email.toLowerCase());
  };

  // Feature flag: when true, auto-copy amount/frequency + supplier into arrears dialog
  // from the parent question-group (legacy behaviour). When false, we keep the
  // arrears dialog independent and only seed safe defaults (notes, single-option type).
  const ENABLE_AUTO_COPY_ARREARS_DEFAULTS = false;

  // Initialize sensible defaults for arrears dialog from the parent group values.
  React.useEffect(() => {
    if (!isArrearsList || !isDialogOpen) return;
    // Only for new arrears (no edit of existing item)
    if (editingIndex !== null) return;
    try {
      const fp = String(fieldName || '');
      const parentPath = fp.includes('.') ? fp.split('.').slice(0, -1).join('.') : '';
      const groupData =
        parentPath && form?.getValues
          ? (form.getValues(parentPath) as Record<string, unknown>)
          : {};
      // Linked fields (if provided) - prefer the new 'repayment*' keys, fallback to old
      const amountPath = resolveFirstLinkedPath(['repaymentAmount', 'amount']);
      const freqPath = resolveFirstLinkedPath(['repaymentFrequency', 'frequency']);
      const notesPath = resolveFirstLinkedPath(['repaymentNotes', 'notes']);
      const supplierPath = resolveLinkedPath('companyName');
      const supplierOtherPath = resolveLinkedPath('companyOtherName');

      const rawAmount = amountPath ? (form?.getValues?.(amountPath) as any) : undefined;
      const rawFreq = freqPath ? (form?.getValues?.(freqPath) as string | undefined) : undefined;
      const rawNotes = notesPath ? (form?.getValues?.(notesPath) as string | undefined) : undefined;
      const rawSupplier = supplierPath
        ? (form?.getValues?.(supplierPath) as string | undefined)
        : undefined;
      const rawSupplierOther = supplierOtherPath
        ? (form?.getValues?.(supplierOtherPath) as string | undefined)
        : undefined;

      // Fallbacks to group values when links are absent
      const amountFromGroup = (groupData?.amount as string | number | undefined) ?? '';
      const freqFromGroup = (groupData?.frequency as string | undefined) ?? '';
      const gasSupplier = (groupData as any)?.gasSupplier;
      const gasSupplierOther = (groupData as any)?.gasSupplierOther;
      const elecSupplier = (groupData as any)?.electricitySupplier;
      const elecSupplierOther = (groupData as any)?.electricitySupplierOther;

      let companyName = rawSupplier ?? '';
      let companyNameManual = rawSupplierOther ?? '';
      if (!supplierPath) {
        if (typeof gasSupplier === 'string') {
          companyName = gasSupplier;
          companyNameManual =
            gasSupplier === 'Other' ? String(gasSupplierOther || '') : '';
        } else if (typeof elecSupplier === 'string') {
          companyName = elecSupplier;
          companyNameManual =
            elecSupplier === 'Other' ? String(elecSupplierOther || '') : '';
        }
      }

      const autoAccountType =
        accountTypeOptions.length === 1 ? String(((accountTypeOptions[0] as any)?.value) ?? '') : '';

      if (ENABLE_AUTO_COPY_ARREARS_DEFAULTS) {
        // Legacy behaviour: auto-copy supplier and amount/frequency into arrears draft
        setArrearsDraft((prev) => ({
          ...prev,
          companyName,
          companyNameManual,
          // Link amount to repaymentAmount (not owed amount)
          repaymentAmount:
            rawAmount !== undefined &&
              rawAmount !== null &&
              String(rawAmount) !== ''
              ? String(rawAmount)
              : typeof amountFromGroup === 'number'
                ? String(amountFromGroup)
                : String(amountFromGroup || ''),
          frequency: (
            rawFreq
              ? mapDebtFrequencyApiToUi(rawFreq) || ''
              : String(freqFromGroup || '')
          ).toLowerCase(),
          notes: rawNotes ?? prev.notes,
          accountType: autoAccountType || prev.accountType,
        }));
      } else {
        // New behaviour: do NOT auto-populate supplier or amount/frequency.
        // Only seed safe defaults (notes and a single-option account type).
        setArrearsDraft((prev) => ({
          ...prev,
          // Ensure a sensible default so the toggle shows a selected value
          frequency: prev.frequency || 'monthly',
          notes: rawNotes ?? prev.notes,
          accountType: autoAccountType || prev.accountType,
        }));
      }
    } catch {
      // ignore
    }
  }, [isArrearsList, isDialogOpen, editingIndex, fieldName, form, accountTypeOptions.length, resolveLinkedPath]);

  // Helper to prefill arrears dialog draft from an existing list item
  const prefillArrearsDraftFromItem = React.useCallback(
    (itemToEdit: ArrearFormItem | undefined) => {
      if (!isArrearsList || !itemToEdit) return;
      try {
        repaymentClearedRef.current = false;
        const existing = itemToEdit as Record<string, unknown>;

        if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[ArrearForm] prefill from existing item', {
            fieldName,
            existing,
            companyOptionsForSelect,
          });
        }

        // Prefer explicit fields; fall back to common aliases used by API payloads
        const amountValue =
          typeof existing.amountCurrentlyOwned === 'object' &&
            (existing.amountCurrentlyOwned as { amount?: unknown })?.amount !==
            undefined
            ? String(
              (existing.amountCurrentlyOwned as { amount?: unknown }).amount ??
              '',
            )
            : String(
              (existing as any).amountCurrentlyOwned ??
              (existing as any).amountOwed ??
              '',
            );
        // Repayment amount should only use explicit repayment fields; do not
        // fall back to the owed amount to avoid overwriting the user's
        // repayment entry when the API omits it.
        const repaymentValue =
          (existing as any).repaymentAmount ??
          (existing as any).repayment ??
          (typeof existing.amountCurrentlyOwned === 'object'
            ? (existing.amountCurrentlyOwned as { repayment?: unknown }).repayment
            : undefined) ??
          '';

        const opts = companyOptionsForSelect;

        const matchCompanyOption = (
          name: string | null | undefined,
        ): CompanyOption | undefined => {
          const key = normalizeCompanyName(name ?? '');
          if (!key) return undefined;
          return opts.find((opt) => {
            const valueKey = normalizeCompanyName(
              String((opt as any).value ?? ''),
            );
            const labelKey = normalizeCompanyName(
              String((opt as any).label ?? ''),
            );
            return valueKey === key || labelKey === key;
          });
        };


        const manualName = String(
          (existing as any).companyNameManual ?? '',
        ).trim();
        const savedName = String(
          (existing as any).companyName ??
          (existing as any).creditor ??
          (existing as any).company ??
          '',
        ).trim();
        // Also check for saved product name (used when product was selected)
        const savedProductName = String((existing as any).productName ?? '').trim();

        let nextCompanyValue = '';
        let nextCompanyManual = '';
        let nextProductName = '';

        // ─────────────────────────────────────────────────────────────────────────
        // Derive parent company and product from saved entry (mirrors DebtCategory.tsx logic)
        // ─────────────────────────────────────────────────────────────────────────
        // Priority:
        // 1. If savedProductName exists, try to find its parent in lenderLookup.childToParent
        // 2. If savedName (creditor/companyName) is a child/product, derive parent from childToParent
        // 3. If savedName is a parent company, use it directly
        // 4. Fall back to manual entry mode

        const candidateForLookup = savedProductName || savedName;

        if (manualName) {
          // Prefer mapping the manual name back to a real option if it now exists.
          const manualMatch = matchCompanyOption(manualName);
          if (manualMatch && !isOtherValue(manualMatch.value)) {
            nextCompanyValue = String((manualMatch as any).value ?? '');
            nextCompanyManual = '';
          } else {
            nextCompanyValue = resolveOtherSelectValue();
            nextCompanyManual = manualName;
          }
        } else if (candidateForLookup) {
          // Try reverse-lookup: check if the candidate is a child/product in lenderLookup
          const childMatch = lenderLookup.childToParent.get(normalizeCompanyName(candidateForLookup));
          if (childMatch) {
            // Found in child lookup - set parent company and product
            const parentCompany = childMatch.parent.parentCompany;
            const productName = childMatch.childRaw?.lender ?? candidateForLookup;
            const parentMatch = matchCompanyOption(parentCompany);
            if (parentMatch && !isOtherValue(parentMatch.value)) {
              nextCompanyValue = String((parentMatch as any).value ?? '');
              nextProductName = productName;
            } else {
              // Parent not in options - fall back to manual with product as company name
              nextCompanyValue = resolveOtherSelectValue();
              nextCompanyManual = candidateForLookup;
            }
          } else {
            // Not a child - check if it's a parent company
            const parentMatch = lenderLookup.byParent.get(normalizeCompanyName(candidateForLookup));
            if (parentMatch) {
              // It's a parent company - use it directly
              const companyMatch = matchCompanyOption(parentMatch.parentCompany);
              if (companyMatch && !isOtherValue(companyMatch.value)) {
                nextCompanyValue = String((companyMatch as any).value ?? '');
                // If there was a saved product name different from parent, try to use it
                if (savedProductName && savedProductName !== parentMatch.parentCompany) {
                  nextProductName = savedProductName;
                }
              } else {
                nextCompanyValue = resolveOtherSelectValue();
                nextCompanyManual = candidateForLookup;
              }
            } else {
              // Not found in lender lookup at all - check dropdown options
              const savedMatch = matchCompanyOption(savedName);
              if (savedMatch && !isOtherValue(savedMatch.value)) {
                nextCompanyValue = String((savedMatch as any).value ?? '');
                nextProductName = savedProductName;
              } else if (!isOtherValue(savedName)) {
                // Not in dropdown - treat as manual entry
                nextCompanyValue = resolveOtherSelectValue();
                nextCompanyManual = savedName;
              } else {
                nextCompanyValue = resolveOtherSelectValue();
                nextCompanyManual = '';
              }
            }
          }
        }

        // Normalize account type against options if provided
        const rawAccType = String(
          (existing as any).accountType ??
          (existing as any).accTypeCode ??
          (existing as any).accountTypeCode ??
          '',
        ).trim();
        const accOpts = Array.isArray(accountTypeOptions)
          ? accountTypeOptions
          : [];
        let nextAccountType = rawAccType;
        if (rawAccType) {
          const accByValue = accOpts.find(
            (o) => String((o as any).value) === rawAccType,
          );
          const accByLabel = accOpts.find(
            (o) =>
              String((o as any).label || '')
                .trim()
                .toLowerCase() === rawAccType.toLowerCase(),
          );
          if (accByValue) {
            nextAccountType = String((accByValue as any).value);
          } else if (accByLabel) {
            nextAccountType = String((accByLabel as any).value);
          }
        }

        // Normalize frequency to our UI tokens if possible (handle API codes like 'M','W','4W')
        const rawFreqAny = (
          (existing as any).frequency ??
          (existing as any).repaymentFrequency ??
          (typeof (existing as any).amountCurrentlyOwned === 'object'
            ? (existing as any).amountCurrentlyOwned?.frequency
            : undefined) ??
          ''
        ) as string | null | undefined;
        const mappedUiFreq = mapDebtFrequencyApiToUi(
          typeof rawFreqAny === 'string' ? rawFreqAny : null,
        );
        const rawFreq = String(mappedUiFreq ?? rawFreqAny ?? '')
          .trim()
          .toLowerCase();
        const normalizedFreq =
          rawFreq === 'weekly'
            ? 'weekly'
            : rawFreq === '4weekly' ||
              rawFreq === 'fourweekly' ||
              rawFreq === 'four-weekly'
              ? '4weekly'
              : rawFreq === 'monthly'
                ? 'monthly'
                : rawFreq === 'quarterly'
                  ? 'quarterly'
                  : rawFreq === 'yearly' || rawFreq === 'annually'
                    ? 'yearly'
                    : rawFreq;

        const rawReference = String(
          (existing as any).referenceNumber ??
          (existing as any).accountNumber ??
          (existing as any).reference ??
          '',
        );
        const isFallback = isEmailFallbackReference(rawReference);

        setArrearsDraft({
          companyName: nextCompanyValue,
          companyNameManual: nextCompanyManual,
          // Use derived product name from lenderLookup, or fall back to saved value
          productName: nextProductName || String((existing as any).productName ?? ''),
          accountType: nextAccountType,
          referenceNumber: isFallback ? '' : rawReference.trim(),
          amountCurrentlyOwned: amountValue,
          repaymentAmount: String(repaymentValue),
          frequency: normalizedFreq,
          notes: String((existing as any).notes ?? ''),
          paymentOfferAmount: String(
            (existing as any).paymentOfferAmount ??
            (existing as any).pymtOfferAmount ??
            '',
          ),
          paymentOfferMode: String(
            (existing as any).paymentOfferMode ??
            (existing as any).pymtOfferMode ??
            '',
          ),
        });

        const existingRef = rawReference.trim();
        setNoReferenceId(isFallback || existingRef === '');
      } catch {
        // ignore prefill errors
      }
    },
    [
      accountTypeOptions,
      companyOptionsForSelect,
      isArrearsList,
      isOtherValue,
      resolveOtherSelectValue,
      lenderLookup,
    ],
  );

  // Global event open handler to force open dialog for specific list-form path
  React.useEffect(() => {
    if (!fieldName) return;
    const handleGlobalOpen = (e: Event) => {
      try {
        const detail = (e as CustomEvent)?.detail as { fieldPath?: string } | undefined;
        if (!detail || typeof detail.fieldPath !== 'string') return;
        if (detail.fieldPath === fieldName) {
          // Decide mode based on existing items
          if (Array.isArray(value) && value.length > 0) {
            setEditingIndex(0);
            prefillArrearsDraftFromItem(value[0] as ArrearFormItem);
          } else {
            setEditingIndex(null);
            // Reset draft when opening with no existing arrears
            setArrearsDraft(createInitialArrearsDraft());
            setNoReferenceId(false);
          }
          setIsDialogOpen(true);
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('iehub-open-ArrearForm-dialog', handleGlobalOpen as EventListener);
    return () => {
      window.removeEventListener('iehub-open-ArrearForm-dialog', handleGlobalOpen as EventListener);
    };
  }, [fieldName, prefillArrearsDraftFromItem, value]);

  // Dialog should not auto-open on load or when `inArrears` changes programmatically.
  // It opens only on explicit user actions: ticking the checkbox or clicking Edit.

  // Keep local checkbox state in sync with the parent "<parent>.inArrears" value
  React.useEffect(() => {
    let cleanup: (() => void) | undefined;
    try {
      if (!form || typeof form.watch !== 'function' || typeof form.getValues !== 'function') {
        return () => { };
      }
      const fieldPath = String(fieldName || '');
      if (!fieldPath.includes('.')) {
        return () => { };
      }
      const parts = fieldPath.split('.');
      parts[parts.length - 1] = 'inArrears';
      const inArrearsPath = parts.join('.');
      // Initial sync
      const current = form.getValues(inArrearsPath);
      if (typeof current === 'boolean') setIsChecked(current);
      // Subscribe to changes
      const sub = form.watch((_values: unknown, { name }: { name?: string }) => {
        if (name === inArrearsPath) {
          try {
            const v = form.getValues(inArrearsPath);
            if (typeof v === 'boolean') setIsChecked(v);
          } catch {
            // ignore
          }
        }
      });
      cleanup = () => {
        try {
          (sub as any)?.unsubscribe?.();
        } catch {
          // ignore
        }
      };
    } catch {
      // ignore
    }
    return () => {
      if (cleanup) cleanup();
    };
  }, [form, fieldName]);

  // Handle checkbox toggle
  const handleCheckboxToggle = () => {
    // If an arrear exists, prevent turning off and show warning
    if (Array.isArray(value) && value.length > 0 && isChecked) {
      setUntickWarning('Please delete the added arrear to turn this off.');
      window.setTimeout(() => setUntickWarning(null), 3000);
      return;
    }
    const newChecked = !isChecked;
    setIsChecked(newChecked);
    // Reflect to parent "<parent>.inArrears" boolean
    try {
      if (form && fieldName) {
        const fieldPath = String(fieldName || '');
        if (fieldPath.includes('.')) {
          const parts = fieldPath.split('.');
          parts[parts.length - 1] = 'inArrears';
          const inArrearsPath = parts.join('.');
          form.setValue(inArrearsPath, newChecked, { shouldValidate: true });
        }
      }
    } catch {
      // no-op
    }
    // Open dialog only when the user explicitly ticks the checkbox to Yes
    if (newChecked) {
      setEditingIndex(null);
      setNoReferenceId(false);
      setIsDialogOpen(true);
    }
    // If unchecking, clear all items
    if (!newChecked && onChange) {
      onChange([]);
    }
  };

  // Keep "inArrears" enforced to true when at least one arrear exists
  React.useEffect(() => {
    try {
      if (!form || !fieldName) return;
      if (!Array.isArray(value)) return;
      const hasArrear = value.length > 0;
      const fieldPath = String(fieldName || '');
      if (!fieldPath.includes('.')) return;
      const parts = fieldPath.split('.');
      parts[parts.length - 1] = 'inArrears';
      const inArrearsPath = parts.join('.');
      if (hasArrear) {
        setIsChecked(true);
        form.setValue(inArrearsPath, true, { shouldValidate: true });
      }
    } catch {
      // ignore
    }
  }, [form, fieldName, Array.isArray(value) ? value.length : 0]);

  // Handle add button click (removed - unused)

  // Handle edit button click
  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    const itemToEdit = value[index];
    // Pre-populate temporary form fields in parent form
    if (form && formFields && itemToEdit) {
      formFields.forEach((field: FieldConfig) => {
        const fieldValue = itemToEdit[field.fieldName];
        form.setValue(`${tempFieldPrefix}.${field.fieldName}`, fieldValue);
      });
    }
    prefillArrearsDraftFromItem(itemToEdit);
    setIsDialogOpen(true);
  };

  // Fetch payment offer ID when editing an existing arrear (mirrors DebtCategory.tsx behaviour)
  React.useEffect(() => {
    if (!isDialogOpen || editingIndex === null || !isArrearsList) return;
    const itemToEdit = value[editingIndex];
    if (!itemToEdit) return;

    // Only fetch if the item has a debt ID and account number
    const numericDebtId =
      typeof (itemToEdit as any).id === 'number'
        ? (itemToEdit as any).id
        : null;
    const accNum = String(
      (itemToEdit as any).referenceNumber ??
      (itemToEdit as any).accountNumber ??
      '',
    ).trim();

    if (!numericDebtId || !accNum) return;

    let ignore = false;
    (async () => {
      try {
        const customerId = await getCustomerId();
        if (!customerId || ignore) return;

        const resp = await DebtsService.getPaymentOffer(
          Number(customerId),
          numericDebtId,
          accNum,
          { skipErrorCapture: true },
        );
        if (ignore) return;

        const payload = (resp?.data as any) ?? null;
        const latest =
          Array.isArray(payload) && payload.length > 0
            ? (payload[payload.length - 1] as any)
            : payload;

        if (latest) {
          setArrearsDraft((prev) => ({
            ...prev,
            paymentOfferAmount:
              typeof latest.paymentOfferAmount === 'number' &&
                !Number.isNaN(latest.paymentOfferAmount)
                ? String(latest.paymentOfferAmount)
                : prev.paymentOfferAmount ?? '',
            paymentOfferMode:
              // UI wants the preferred payment day, not the backend constant "M".
              latest.paymentPreferredFrequency ??
              latest.paymentOfferMode ??
              prev.paymentOfferMode ??
              '',
          }));
          // This value came from API; treat as not user-cleared.
          paymentOfferClearedRef.current = false;
          // Capture baseline offer amount for budget calculations (avoid double subtract).
          if (!originalOfferAmountSetRef.current) {
            const amt =
              typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
                ? latest.paymentOfferAmount
                : 0;
            originalOfferAmountForBudgetRef.current = amt;
            originalOfferAmountSetRef.current = true;
          }
          // Store the payment offer ID in the item for later update
          if (typeof latest.id === 'number' && latest.id > 0) {
            (itemToEdit as any).paymentOfferId = latest.id;
          }
        }
      } catch {
        // ignore - dialog will continue with whatever we already have
      }
    })();

    return () => {
      ignore = true;
    };
  }, [isDialogOpen, editingIndex, isArrearsList, value]);

  // Handle delete icon click - open confirmation dialog
  const handleDeleteClick = (index: number) => {
    setPendingDeleteIndex(index);
    setConfirmDeleteOpen(true);
  };

  // Perform actual delete after confirmation
  const performDelete = async (index: number) => {
    const item = value[index];
    // Attempt server-side delete when an id exists
    try {
      const numericId = typeof (item as any)?.id === 'number' ? (item as any).id : null;
      if (numericId && numericId > 0) {
        const customerId = await getCustomerId();
        if (customerId) {
          // IMPORTANT (legacy parity):
          // Before deleting a debt, delete any pending payment offer so pendingPaymentOffer totals clear.
          try {
            const directOfferId =
              typeof (item as any)?.paymentOfferId === 'number' && (item as any).paymentOfferId > 0
                ? ((item as any).paymentOfferId as number)
                : null;
            const accNum = String(
              (item as any)?.referenceNumber ??
                (item as any)?.accountNumber ??
                '',
            ).trim();

            let offerId: number | null = directOfferId;
            if (!offerId && accNum) {
              const offerResp = await DebtsService.getPaymentOffer(
                Number(customerId),
                Number(numericId),
                accNum,
                { skipErrorCapture: true },
              );
              const payload = (offerResp?.data as any) ?? null;
              const latest =
                Array.isArray(payload) && payload.length > 0
                  ? (payload[payload.length - 1] as any)
                  : payload;
              offerId = typeof latest?.id === 'number' && latest.id > 0 ? latest.id : null;
            }

            if (offerId) {
              await DebtsService.deletePaymentOffer(offerId, { skipErrorCapture: true });
            }
          } catch {
            // best-effort: do not block delete if offer delete fails
          }

          await DebtsService.deleteDebt(Number(customerId), Number(numericId), { skipErrorCapture: true });
        }
      }
    } catch {
      // ignore API deletion failure; proceed with local removal
    }

    if (onChange) {
      const newItems = value.filter((_, i) => i !== index);
      onChange(newItems);

      // Set arrears toggle to false on delete as requested
      try {
        if (form && fieldName) {
          const fieldPath = String(fieldName || '');
          if (fieldPath.includes('.')) {
            const parts = fieldPath.split('.');
            parts[parts.length - 1] = 'inArrears';
            const inArrearsPath = parts.join('.');
            form.setValue(inArrearsPath, false, { shouldValidate: true });
          }
        }
      } catch {
        // no-op
      }

      // If no items left and checkbox is shown, uncheck it
      if (newItems.length === 0 && showAsCheckBox) {
        setIsChecked(false);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteIndex === null) {
      setConfirmDeleteOpen(false);
      return;
    }
    setIsDeleting(true);
    try {
      await performDelete(pendingDeleteIndex);
    } finally {
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
      setPendingDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setPendingDeleteIndex(null);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    // Sync dialog values back to linked non-supplier fields (new arrears only)
    try {
      if (isArrearsList && editingIndex === null && linkComponentToArrearsProps) {
        const amountPath = resolveFirstLinkedPath(['repaymentAmount', 'amount']);
        const freqPath = resolveFirstLinkedPath(['repaymentFrequency', 'frequency']);
        const notesPath = resolveFirstLinkedPath(['repaymentNotes', 'notes']);

        if (amountPath) {
          // Write back repayment amount, not owed amount
          form?.setValue?.(amountPath, String(arrearsDraft.repaymentAmount ?? ''), { shouldValidate: true });
        }
        if (freqPath) {
          const freqApi = mapDebtFrequencyUiToApi(arrearsDraft.frequency) ?? null;
          form?.setValue?.(freqPath, freqApi, { shouldValidate: true });
        }
        if (notesPath) {
          form?.setValue?.(notesPath, String(arrearsDraft.notes ?? ''), { shouldValidate: true });
        }
      }
    } catch {
      // ignore link sync errors
    }

    // If the dialog was closed without saving, revert draft to a clean state
    if (!wasSavedRef.current) {
      setArrearsDraft(createInitialArrearsDraft());
      setNoReferenceId(false);
    }

    setIsDialogOpen(false);
    setEditingIndex(null);
    // Clear temporary form fields in parent form
    if (form && formFields) {
      formFields.forEach((field: FieldConfig) => {
        form.setValue(`${tempFieldPrefix}.${field.fieldName}`, undefined);
      });
    }

    // If dialog was closed without saving and there are no items, revert arrears toggle to No
    try {
      if (!wasSavedRef.current && Array.isArray(value) && value.length === 0 && form && fieldName) {
        const fieldPath = String(fieldName || '');
        if (fieldPath.includes('.')) {
          const parts = fieldPath.split('.');
          parts[parts.length - 1] = 'inArrears';
          const inArrearsPath = parts.join('.');
          // Set the arrears toggle to false
          form.setValue(inArrearsPath, false, { shouldValidate: true });
        }
      }
    } catch {
      // no-op if we can't reset the toggle
    } finally {
      wasSavedRef.current = false;
      setAttemptedSubmit(false);
    }
  };

  // Handle form save
  const handleSave = () => {
    wasSavedRef.current = true;
    // Collect data from temporary form fields
    const collectedData: ArrearFormItem = {};
    if (form && formFields) {
      formFields.forEach((field: FieldConfig) => {
        const fieldValue = form.getValues(`${tempFieldPrefix}.${field.fieldName}`);
        if (fieldValue !== undefined) {
          collectedData[field.fieldName] = fieldValue;
        }
      });
    }
    if (onChange) {
      let newItems: ArrearFormItem[];

      if (editingIndex !== null) {
        // Update existing item
        newItems = value.map((item, i) => (i === editingIndex ? collectedData : item));
      } else {
        // Add new item
        newItems = [...value, collectedData];
      }

      onChange(newItems);
    }

    handleDialogClose();
  };

  // Handle cancel
  const handleCancel = () => {
    handleDialogClose();
  };

  // Show add button condition
  const hasCheckbox = Boolean(showAsCheckBox || isArrearsList);

  // Get display text for list item
  const getItemDisplayText = (item: ArrearFormItem) => {
    // Determine company name - prefer product name when available (like DebtCategory)
    // Use manual name if "Other" or "I can't find" is selected
    const rawCompanyName = item.companyName || item.company || '';
    let displayCompanyName = rawCompanyName;

    // Check if the company is "Other" or matches "I can't find" pattern
    if (isOtherValue(rawCompanyName) && item.companyNameManual) {
      displayCompanyName = String(item.companyNameManual).trim();
    } else if (item.productName && String(item.productName).trim()) {
      // Prefer product name when available (e.g., "Barclays Bank UK" instead of "Barclays Bank")
      displayCompanyName = String(item.productName).trim();
    } else if (!displayCompanyName) {
      displayCompanyName = 'Unknown Company';
    }

    const rawReference = item.referenceNumber || item.reference || '';
    const reference = isEmailFallbackReference(rawReference)
      ? "I don't have any Reference ID"
      : rawReference;

    // Handle both direct amountCurrentlyOwned and nested amountCurrentlyOwned.amount structure.
    // Guard against accidentally stringifying the whole object when the nested amount is empty.
    const resolveNumericLikeValue = (value: unknown): string => {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'number' || typeof value === 'string') {
        const asString = String(value).trim();
        return asString;
      }
      if (typeof value === 'object') {
        // Support legacy { amount, frequency } shapes
        const maybeAmount =
          (value as { amount?: unknown }).amount !== undefined
            ? (value as { amount?: unknown }).amount
            : undefined;
        if (typeof maybeAmount === 'number' || typeof maybeAmount === 'string') {
          const asString = String(maybeAmount).trim();
          return asString;
        }
      }
      return '';
    };

    const amountSource =
      item.amountCurrentlyOwned !== undefined
        ? item.amountCurrentlyOwned
        : item.amount;
    const amount = resolveNumericLikeValue(amountSource);

    // Get repayment amount and frequency
    const repaymentAmount = resolveNumericLikeValue(
      item.repaymentAmount !== undefined
        ? item.repaymentAmount
        : (item as any).repayment !== undefined
          ? (item as any).repayment
          : typeof item.amountCurrentlyOwned === 'object'
            ? (item.amountCurrentlyOwned as { repayment?: unknown }).repayment
            : undefined
    );
    // Prefer UI token; fall back to API code and map to UI token; also support nested amountCurrentlyOwned.frequency
    const rawFrequency =
      item.frequency ||
      item.repaymentFrequency ||
      (typeof item.amountCurrentlyOwned === 'object' ? item.amountCurrentlyOwned?.frequency : undefined) ||
      '';
    const frequencyUi =
      mapDebtFrequencyApiToUi(typeof rawFrequency === 'string' ? rawFrequency : null) ||
      String(rawFrequency || '');

    // Format frequency to title case for display
    const formatFrequency = (freq: string): string => {
      if (!freq) return '';
      const freqLower = freq.toLowerCase();
      if (freqLower === '4weekly' || freqLower === 'fourweekly' || freqLower === 'four-weekly') {
        return '4-Weekly';
      }
      return freq.charAt(0).toUpperCase() + freq.slice(1).toLowerCase();
    };
    const formattedFrequency = formatFrequency(frequencyUi);

    // Build subtitle parts
    const subtitleParts = [];
    if (reference) subtitleParts.push(`REF: ${reference}`);
    if (amount) subtitleParts.push(`Amount owed : £${amount}`);
    // Only show repayment info if repayment amount exists
    if (repaymentAmount) {
      if (formattedFrequency) {
        subtitleParts.push(`Repayment: £${repaymentAmount} / ${formattedFrequency}`);
      } else {
        subtitleParts.push(`Repayment: £${repaymentAmount}`);
      }
    }

    return {
      title: displayCompanyName,
      subtitle: subtitleParts.join(' | '),
    };
  };

  const applyCompanySelection = React.useCallback(
    (rawValue: string) => {
      const next = String(rawValue || '');

      // Keep identical behavior to the Company-name dropdown selection, including duplicate warning.
      if (
        !editingIndex &&
        !isOtherValue(next) &&
        existingCreditorNameSet.size > 0
      ) {
        const normalizedNext = normalizeCompanyName(next);
        if (normalizedNext && existingCreditorNameSet.has(normalizedNext)) {
          const now = Date.now();
          if (
            !lastDuplicateCompanyAlertAtRef.current ||
            now - lastDuplicateCompanyAlertAtRef.current >= 1500
          ) {
            lastDuplicateCompanyAlertAtRef.current = now;
            try {
              window.dispatchEvent(
                new CustomEvent('iehub-alert', {
                  detail: {
                    severity: 'warning',
                    message: `You already have a debt listed for ${next}.`,
                    variant: 'filled',
                    duration: 3500,
                  },
                }),
              );
            } catch {
              // no-op
            }
          }
        }
      }

      setArrearsDraft((p) => ({
        ...p,
        companyName: next,
        // keep manual value when selecting "Other/can't find"; otherwise clear it
        companyNameManual: isOtherValue(next) ? p.companyNameManual : '',
        productName: '',
      }));
    },
    [editingIndex, existingCreditorNameSet, isOtherValue],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Checkbox (if showAsCheckBox is true and label provided) */}
      {hasCheckbox && !!label && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={handleCheckboxToggle}
        >
          {isChecked ? (
            <CheckBoxRoundedIcon
              sx={{
                fontSize: '20px',
                marginRight: 1,
                color: '#2e7d32',
              }}
            />
          ) : (
            <CheckBoxOutlineBlankRoundedIcon
              sx={{
                fontSize: '20px',
                marginRight: 1,
                color: 'grey.500',
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontSize: isMobile ? '14px' : '16px',
              lineHeight: isMobile ? '20px' : '22px',
              userSelect: 'none',
            }}
          >
            {label}
          </Typography>
        </Box>
      )}
      {/* If no explicit label provided, show a sensible default for arrears lists */}
      {hasCheckbox && !label && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={handleCheckboxToggle}
        >
          {isChecked ? (
            <CheckBoxRoundedIcon
              sx={{
                fontSize: '20px',
                marginRight: 1,
                color: '#2e7d32',
              }}
            />
          ) : (
            <CheckBoxOutlineBlankRoundedIcon
              sx={{
                fontSize: '20px',
                marginRight: 1,
                color: 'grey.500',
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontSize: isMobile ? '14px' : '16px',
              lineHeight: isMobile ? '20px' : '22px',
              userSelect: 'none',
            }}
          >
            Are you currently in arrears?
          </Typography>
        </Box>
      )}
      {untickWarning && (
        <Typography
          variant="body2"
          sx={{
            color: 'warning.main',
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: isMobile ? '16px' : '20px',
            mt: -1,
            mb: 1,
          }}
        >
          {untickWarning}
        </Typography>
      )}





      {/* List Items */}
      {value.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {value.map((item, index) => {
            const displayText = getItemDisplayText(item);
            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: 'white',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: isMobile ? '14px' : '16px',
                      lineHeight: isMobile ? '20px' : '22px',
                      fontWeight: 600,
                      color: 'text.primary',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {displayText.title}
                  </Typography>
                  {displayText.subtitle && (
                    <Typography
                      data-private
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        lineHeight: isMobile ? '16px' : '20px',
                        color: 'text.secondary',
                      }}
                    >
                      {displayText.subtitle}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditClick(index)}
                    sx={{
                      color: '#757575',
                      padding: '6px',
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(index)}
                    sx={{
                      color: '#e53935',
                      padding: '6px',
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Error message - matching TextField.tsx error handling pattern */}
      {(error || helperText) && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            mt: 1,
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: isMobile ? '16px' : '20px',
            fontWeight: 500,
            display: 'block',
          }}
        >
          {helperText || 'This field is required'}
        </Typography>
      )}

      {/* Dialog/Bottom Sheet */}
      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        fullScreen={isMobile}
        {...(isMobile && { TransitionComponent: Transition })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '12px',
            ...(isMobile && {
              margin: 0,
              maxHeight: '90vh',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              borderRadius: '16px 16px 0 0',
            }),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            pt: 2.5,
            px: 3,
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: isMobile ? '18px' : '20px',
              lineHeight: isMobile ? '24px' : '28px',
              color: theme.palette.text.primary,
            }}
          >
            {editingIndex !== null ? 'Edit arrears' : formTitle}
          </Typography>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: isMobile ? 2 : 3,  // 16px mobile, 24px desktop
            pb: isMobile ? 2 : 3,
            pt: 1,
          }}
        >
          {isArrearsList ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 2.25 : 2.5,
            }}>
              {/* Company name */}
              <Select
                label="Company name"
                dialogLabel="Select a Company"
                options={companyOptionsForSelect}
                value={arrearsDraft.companyName}
                validation={{ required: true, requiredMessage: 'Company name is required' }}
                error={attemptedSubmit && !isCompanySelected}
                onChange={(val) => {
                  applyCompanySelection(String(val || ''));
                }}
                searchable
                fullWidth
                showErrorBelow={false}
                placeholder="Select a Company"
                dataPrivate={false}
              />
              {(isOtherValue(arrearsDraft.companyName) || arrearsDraft.companyName === '__manual__') && (
                <FreeSelect
                  label="Other Company name"
                  options={[]}
                  value={arrearsDraft.companyNameManual}
                  dataPrivate={false}
                  onChange={(nextValue) => {
                    const manual =
                      typeof nextValue === 'string'
                        ? nextValue
                        : nextValue != null
                          ? String(nextValue)
                          : '';

                    // Always reflect what the user typed (prevents "last character removed" snapback).
                    setArrearsDraft((p) => ({
                      ...p,
                      companyNameManual: manual,
                    }));

                    // If manual text exactly matches a real Company option (case-insensitive),
                    // auto-select it and behave exactly like a normal company selection.
                    const normalizedTyped = normalizeCompanyName(manual);
                    const matchedCompany =
                      normalizedTyped
                        ? companyOptionsForSelect.find((opt) => {
                          if (isOtherValue(opt.value)) return false;
                          const valueKey = normalizeCompanyName(opt.value);
                          const labelKey = normalizeCompanyName(opt.label);
                          return valueKey === normalizedTyped || labelKey === normalizedTyped;
                        })
                        : undefined;

                    if (matchedCompany) {
                      applyCompanySelection(String(matchedCompany.value));
                    }
                  }}
                  validation={{ required: true }}
                  deferValidation={true}
                  error={attemptedSubmit && companyRequiresManual && !isManualProvided}
                  placeholder="Enter company name"
                  helperTop="Start typing to search our Freepost list, or enter the company name manually"
                  fullWidth
                  loadFreepostLenders={true}
                />
              )}

              {/* Product name (conditional based on lender metadata) */}
              {shouldShowProductSelect && (
                <Select
                  label="Product name"
                  dialogLabel="Select a Product"
                  options={effectiveProductOptions}
                  value={arrearsDraft.productName}
                  onChange={(val) =>
                    setArrearsDraft((p) => ({ ...p, productName: String(val || '') }))
                  }
                  error={Boolean(productSelectError)}
                  helperText={productSelectError ? 'Please select a product' : undefined}
                  fullWidth
                  showErrorBelow={true}
                  placeholder="Select a product"
                  dataPrivate={false}
                  {...(isProductRequired && { validation: { required: true } })}
                />
              )}

              {/* Reference number */}
              <SpecialTextField
                label={arrearsReferenceLabel}
                placeholder={arrearsSelectionContext.referencePlaceholder}
                value={arrearsDraft.referenceNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleReferenceValueChange(e.target.value ?? '')
                }
                inputProps={{
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === "'") {
                      event.preventDefault();
                      triggerBlockedCharacterAlert();
                    }
                  },
                  onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
                    const pastedText = event.clipboardData.getData('text');
                    const selectionStart = event.currentTarget.selectionStart || 0;
                    const selectionEnd = event.currentTarget.selectionEnd || 0;
                    const currentLength = event.currentTarget.value.length;
                    const maxLength = 50; 

                    if (currentLength - (selectionEnd - selectionStart) + pastedText.length > maxLength) {
                         triggerBlockedCharacterAlert(true);
                    }
                  }
                }}
                allowNoValue={true}
                noValueCheckboxLabel={arrearsNoValueCheckboxLabel}
                noValueChecked={noReferenceId}
                onNoValueChange={(checked) => {
                  setNoReferenceId(checked);
                  if (checked) {
                    setArrearsDraft((p) => ({
                      ...p,
                      referenceNumber: '',
                    }));
                  }
                }}
                helpLinkText={arrearsReferenceHelpLinkText}
                helpDialog={arrearsReferenceHelpDialog}
                showErrorBelow={true}
              />

              {/* Arrears Type */}
              {Array.isArray(accountTypeOptions) && accountTypeOptions.length > 0 && (
                <Select
                  label="Arrears Type"
                  dialogLabel="Select arrears type"
                  options={accountTypeOptions}
                  value={arrearsDraft.accountType}
                  onChange={(val) =>
                    setArrearsDraft((p) => ({ ...p, accountType: String(val || '') }))
                  }
                  fullWidth
                  showErrorBelow={true}
                  placeholder="Select arrears type"
                  dataPrivate={false}
                />
              )}

              {/* Amount currently owed */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2.25 : 2.5 }}>
                <Typography variant="subtitle1" sx={{
                  fontWeight: 500,
                  fontSize: isMobile ? '14px' : '16px',
                  lineHeight: isMobile ? '20px' : '22px',
                }}>
                  Amount currently owed
                </Typography>
                <TextWithTooltip
                  value={arrearsDraft.amountCurrentlyOwned}
                  onChange={(val: string) =>
                    setArrearsDraft((p) => ({ ...p, amountCurrentlyOwned: String(val ?? '') }))
                  }
                  placeholder=""
                />
              </Box>

              {/* Repayment amounts */}
              <QuestionGroup
                label="Repayment amounts"
                showCalculatedIncome={true}
                amountValue={arrearsDraft.repaymentAmount}
                frequencyValue={arrearsDraft.frequency}
                hideContainerBorder
                disableCalculatedOverlap
              >
                <TextWithTooltip
                  value={arrearsDraft.repaymentAmount}
                  onChange={(val: string) => {
                    const next = String(val ?? '');
                    // Mark explicit clear so we can persist 0 on save.
                    repaymentClearedRef.current = next.trim() === '';
                    setArrearsDraft((p) => ({ ...p, repaymentAmount: next } as any));
                  }}
                  placeholder=""
                />
                <Box>
                  <SelectToggle
                    label="How often?"
                    options={(arrearsConfig?.repaymentFrequencyOptions ?? REPAYMENT_OPTIONS).map((opt) => ({
                      value: String(opt.value),
                      label: String(opt.label),
                    }))}
                    value={arrearsDraft.frequency}
                    onChange={(val) =>
                      setArrearsDraft((p) => ({ ...p, frequency: String(val || '') }))
                    }
                  />
                </Box>
              </QuestionGroup>

              {/* Payment Offer (conditional, mirrors Debts behaviour) */}
              {showPaymentOfferPanel && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontSize: isMobile ? '14px' : '16px', lineHeight: isMobile ? '20px' : '22px' }}
                  >
                    Payment offer
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px', mb: 0.5 }}>
                        Amount
                      </Typography>
                      <TextWithTooltip
                        value={arrearsDraft.paymentOfferAmount ?? ''}
                        onChange={(val: string) => {
                          const next = String(val ?? '');
                          paymentOfferClearedRef.current = next.trim() === '';
                          setArrearsDraft((p) => ({
                            ...p,
                            paymentOfferAmount: next,
                          }));
                        }}
                        placeholder=""
                      />
                      {paymentOfferErrorMessage && (
                        <Typography sx={{ fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px', color: 'error.main', mt: 0.5 }}>
                          {paymentOfferErrorMessage}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Select
                        label="Preferred payment day"
                        dialogLabel="Preferred payment day"
                        options={[
                          ...Array.from({ length: 31 }).map((_, i) => ({
                            value: String(i + 1),
                            label: String(i + 1),
                          })),
                        ]}
                        value={arrearsDraft.paymentOfferMode ?? ''}
                        onChange={(val) =>
                          setArrearsDraft((p) => ({
                            ...p,
                            paymentOfferMode: String(val || ''),
                          }))
                        }
                        placeholder="Please select"
                        showErrorBelow={false}
                        fullWidth
                      />
                    </Box>
                  </Box>
                  {typeof availableBudget === 'number' && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        lineHeight: isMobile ? '16px' : '20px',
                        color: paymentOfferExceedsBudget ? 'error.main' : '#6b7a7b',
                        mt: 0.5,
                      }}
                    >
                      Amount available in your budget{' '}
                      {formatCurrency(
                        (effectiveAvailableBudget ??
                          availableBudget ??
                          0) as number,
                      ).replace('££', '£')}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Notes */}
              <Box>
                <CollapsibleTextArea
                  label="Notes (optional)"
                  value={arrearsDraft.notes}
                  onChange={(val: string) => setArrearsDraft((p) => ({ ...p, notes: String(val) }))}
                  onKeyDown={(e) => {
                    if (
                      (arrearsDraft.notes ?? '').length >= 200 &&
                      e.key.length === 1 &&
                      !e.ctrlKey &&
                      !e.metaKey
                    ) {
                      showAlert({
                        severity: 'warning',
                        message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                      });
                    }
                  }}
                  onPaste={(e) => {
                    const pastedText = e.clipboardData.getData('text');
                    const selectionStart = e.currentTarget.selectionStart || 0;
                    const selectionEnd = e.currentTarget.selectionEnd || 0;
                    const currentLength = (arrearsDraft.notes ?? '').length;
                    const maxLength = 200;

                    if (currentLength - (selectionEnd - selectionStart) + pastedText.length > maxLength) {
                      showAlert({
                        severity: 'warning',
                        message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                      });
                    }
                  }}
                  maxLength={200}
                  placeholder="Add notes..."
                />
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 3,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    flex: { xs: 1, md: 'initial' },
                    minWidth: { xs: 'auto', md: '140px' },
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '16px',
                    fontWeight: 500,
                    textTransform: 'none',
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setAttemptedSubmit(true);
                    if (!isCompanyValid) {
                      return;
                    }
                    // Mirror Debts behaviour: when lender/product rules require a product selection,
                    // block saving until a product is chosen.
                    if (
                      isProductRequired &&
                      String(arrearsDraft.productName || '').trim().length === 0
                    ) {
                      return;
                    }
                    if (!arrearsDraft.referenceNumber || arrearsDraft.referenceNumber.trim() === '') {
                      setNoReferenceId(true);
                    }
                    if (paymentOfferExceedsBudget) {
                      return;
                    }
                    setIsSaving(true);
                    try {
                      // Compute effective creditor name similar to DebtCategory.tsx
                      // - For manual company selection, use the manual name
                      // - Otherwise, prefer product name (effectiveCreditorName from selectionContext)
                      const effectiveCreditorName = companyRequiresManual
                        ? String(arrearsDraft.companyNameManual || '').trim()
                        : arrearsSelectionContext.effectiveCreditorName?.trim() || resolvedCompanyNameForDisplay;

                      // Build local entry
                      const entry: ArrearFormItem = {
                        ...(editingIndex !== null && value[editingIndex] ? value[editingIndex] : {}),
                        companyName: effectiveCreditorName,
                        companyNameManual: arrearsDraft.companyNameManual,
                        productName: arrearsDraft.productName,
                        accountType: arrearsDraft.accountType,
                        // If the user leaves Reference Number blank (or uses the
                        // "I don't have any Reference ID" path), fall back to the
                        // logged-in email address so that Debts + sharing flows
                        // still receive a stable identifier.
                        referenceNumber: (() => {
                          const rawRef = (arrearsDraft.referenceNumber ?? '').trim();
                          if (rawRef.length > 0) {
                            return rawRef;
                          }
                          const email =
                            typeof user?.emailAddress === 'string'
                              ? user.emailAddress.trim()
                              : '';
                          return email || '';
                        })(),
                        amountCurrentlyOwned: arrearsDraft.amountCurrentlyOwned,
                        repaymentAmount: arrearsDraft.repaymentAmount,
                        frequency: arrearsDraft.frequency,
                        notes: arrearsDraft.notes,
                        // Include payment offer fields for local state persistence
                        paymentOfferAmount: arrearsDraft.paymentOfferAmount,
                        paymentOfferMode: arrearsDraft.paymentOfferMode,
                      };

                      // Persist via Debts API (source: customer_expenses)
                      const customerId = await getCustomerId();
                      const accTypeCode = resolveAccTypeCode(fieldName);
                      // Use the same effectiveCreditorName for API (mirrors DebtCategory.tsx behavior)
                      const creditor = effectiveCreditorName;
                      // Map owed and repayment amounts separately
                      const amountOwedNum = parseAmount(
                        arrearsDraft.amountCurrentlyOwned,
                      );
                      const repaymentNum = parseAmount(
                        arrearsDraft.repaymentAmount,
                      );
                      const repaymentNumForApi =
                        repaymentNum === null && repaymentClearedRef.current
                          ? 0
                          : repaymentNum;
                      const uiFrequency = (arrearsDraft.frequency ||
                        null) as DebtRepaymentFrequencyUi | null;
                      const frequencyApi =
                        mapDebtFrequencyUiToApi(uiFrequency) ?? null;
                      const repaymentMonthly = toMonthly(
                        repaymentNumForApi,
                        uiFrequency,
                      );
                      const fallbackEmail =
                        typeof user?.emailAddress === 'string'
                          ? user.emailAddress.trim()
                          : '';
                      const accountNumberValue =
                        (entry.referenceNumber &&
                          String(entry.referenceNumber).trim()) ||
                        fallbackEmail ||
                        '';
                      const normalize = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
                      const originalItem = editingIndex !== null ? (value[editingIndex] as any) : undefined;
                      const originalSourceRaw = (entry as any).source ?? originalItem?.source;
                      const baseSource = originalSourceRaw ?? 'customer_expenses';
                      const isReferralDebt = (() => {
                        const src = normalize(originalSourceRaw);
                        return src === 'customer_profile' || src === 'referred';
                      })();
                      const originalCreditor = normalize(
                        originalItem?.creditor ??
                        originalItem?.companyName ??
                        originalItem?.lenderName,
                      );
                      const nextCreditor = normalize(creditor);
                      const originalAccType = normalize(originalItem?.accTypeCode ?? originalItem?.accountType);
                      const nextAccType = normalize(accTypeCode ?? undefined);
                      const companyChanged = isReferralDebt && originalCreditor !== nextCreditor;
                      const accTypeChanged = isReferralDebt && originalAccType !== nextAccType;
                      const resolvedSource =
                        isReferralDebt && (companyChanged || accTypeChanged)
                          ? 'unlink_customer_profile'
                          : baseSource;

                      // Coerce any existing debt id (number or string) so that edits to
                      // referred/company-managed arrears use updateDebt (PUT) instead of createDebt (POST).
                      const coerceNumericId = (value: unknown): number | null => {
                        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
                          return value;
                        }
                        if (typeof value === 'string') {
                          const trimmed = value.trim();
                          if (!trimmed) return null;
                          const parsed = Number(trimmed);
                          if (Number.isFinite(parsed) && parsed > 0) {
                            return parsed;
                          }
                        }
                        return null;
                      };

                      const existingId = coerceNumericId(
                        (entry as { id?: number | string | null })?.id ??
                        (originalItem as ({ id?: number | string | null } | undefined))?.id,
                      );

                      const payload: DebtUpsertRequest = {
                        ...(typeof existingId === 'number' && Number.isFinite(existingId) && existingId > 0
                          ? { id: existingId }
                          : {}),
                        customerId: Number(customerId),
                        creditor,
                        accTypeCode: accTypeCode ?? null,
                        // Always send a non-empty accountNumber when possible:
                        // - explicit reference number from the dialog, OR
                        // - fallback to the user's email address when left blank.
                        accountNumber: accountNumberValue,
                        amountOwed: amountOwedNum ?? null,
                        // Persist repayment as the scheduled payment, distinct from amount owed
                        repayment: repaymentNumForApi ?? null,
                        frequency: frequencyApi,
                        notes: String(entry.notes || ''),
                        source: resolvedSource,
                        debtType: 'PRIORITY_DEBTS',
                        repaymentMonthly,
                      };

                      let savedId: number | undefined;
                      let savedDebt: CustomerDebtRecord | undefined;
                      try {
                        if (typeof existingId === 'number' && Number.isFinite(existingId) && existingId > 0) {
                          const resp = await DebtsService.updateDebt(payload, {
                            skipErrorCapture: true,
                          });
                          savedDebt = resp?.data as CustomerDebtRecord | undefined;
                        } else {
                          const resp = await DebtsService.createDebt(payload, {
                            skipErrorCapture: true,
                          });
                          savedDebt = resp?.data as CustomerDebtRecord | undefined;
                        }

                        if (savedDebt && typeof savedDebt.id === 'number') {
                          savedId = savedDebt.id;
                          if (customerId) {
                            try {
                              const lookups = await SharingOrchestrator.fetchLookups();
                              const existingSharing =
                                await SharingOrchestrator.fetchExisting(Number(customerId));
                              await SharingOrchestrator.upsertForDebt(
                                Number(customerId),
                                savedDebt,
                                lookups,
                                existingSharing,
                                { freepostNotes },
                              );
                            } catch {
                              // Ignore sharing failures; arrears save should still succeed.
                            }
                          }

                          // Save payment offer if provided (mirrors DebtCategory.tsx behaviour)
                          const paymentOfferAmountNum = parseAmount(arrearsDraft.paymentOfferAmount);
                          // Get existing payment offer ID from the entry being edited (if any)
                          const existingPaymentOfferId =
                            typeof (entry as any).paymentOfferId === 'number'
                              ? (entry as any).paymentOfferId
                              : undefined;

                          if (paymentOfferAmountNum !== null && paymentOfferAmountNum > 0) {
                            try {
                              const resolvedSchemaNameRaw =
                                (arrearsSelectionContext?.selectedChildRaw as any)?.schemaName ??
                                (arrearsSelectionContext?.parentRecord?.raw as any)?.schemaName ??
                                (savedDebt as any)?.schemaName ??
                                null;
                              const resolvedSchemaName =
                                typeof resolvedSchemaNameRaw === 'string' && resolvedSchemaNameRaw.trim().length > 0
                                  ? resolvedSchemaNameRaw
                                  : null;

                              const poPayload: PaymentOfferUpsertRequest = {
                                customerId: Number(customerId),
                                debtId: savedDebt.id,
                                lenderName: savedDebt.lenderName || savedDebt.creditor || creditor,
                                accountNumber: savedDebt.accountNumber ?? accountNumberValue ?? null,
                                schemaName: resolvedSchemaName,
                                paymentOfferAmount: paymentOfferAmountNum,
                                // Legacy parity: paymentOfferMode is always "M" (monthly); the user's selection
                                // (UI "Preferred payment day") maps to paymentPreferredFrequency.
                                paymentOfferMode: 'M' as any,
                                paymentPreferredFrequency: (arrearsDraft.paymentOfferMode || null) as any,
                                userId: typeof user?.userId === 'number' ? user.userId : null,
                                lastUpdatedBy: typeof user?.userId === 'number' ? user.userId : null,
                                ...(typeof existingPaymentOfferId === 'number' && existingPaymentOfferId > 0
                                  ? { id: existingPaymentOfferId }
                                  : {}),
                              };

                              if (typeof poPayload.id === 'number' && poPayload.id > 0) {
                                await DebtsService.updatePaymentOffer(poPayload.id, poPayload, {
                                  skipErrorCapture: true,
                                });
                              } else {
                                const createdPo = await DebtsService.createPaymentOffer(poPayload, {
                                  skipErrorCapture: true,
                                });
                                const createdPoData = createdPo?.data as any;
                                if (createdPoData && typeof createdPoData.id === 'number') {
                                  // Store the payment offer ID in the entry for future updates
                                  (entry as any).paymentOfferId = createdPoData.id;
                                }
                              }
                            } catch {
                              // Debug visibility: don't silently fail payment offer saves.
                              // eslint-disable-next-line no-console
                              console.warn('[ArrearForm] Payment offer upsert failed', { existingPaymentOfferId });
                            }
                          } else if (
                            paymentOfferClearedRef.current &&
                            typeof existingPaymentOfferId === 'number' &&
                            existingPaymentOfferId > 0
                          ) {
                            // User cleared the offer: update to 0 so backend pendingPaymentOffer updates.
                            try {
                              const resolvedSchemaNameRaw =
                                (arrearsSelectionContext?.selectedChildRaw as any)?.schemaName ??
                                (arrearsSelectionContext?.parentRecord?.raw as any)?.schemaName ??
                                (savedDebt as any)?.schemaName ??
                                null;
                              const resolvedSchemaName =
                                typeof resolvedSchemaNameRaw === 'string' && resolvedSchemaNameRaw.trim().length > 0
                                  ? resolvedSchemaNameRaw
                                  : null;

                              const poPayload: PaymentOfferUpsertRequest = {
                                customerId: Number(customerId),
                                debtId: savedDebt.id,
                                lenderName: savedDebt.lenderName || savedDebt.creditor || creditor,
                                accountNumber: savedDebt.accountNumber ?? accountNumberValue ?? null,
                                schemaName: resolvedSchemaName,
                                paymentOfferAmount: 0,
                                paymentOfferMode: 'M' as any,
                                paymentPreferredFrequency: null,
                                userId: typeof user?.userId === 'number' ? user.userId : null,
                                lastUpdatedBy: typeof user?.userId === 'number' ? user.userId : null,
                                id: existingPaymentOfferId,
                              };

                              await DebtsService.updatePaymentOffer(existingPaymentOfferId, poPayload, {
                                skipErrorCapture: true,
                              });
                            } catch {
                              // eslint-disable-next-line no-console
                              console.warn('[ArrearForm] Payment offer clear-to-0 failed', { existingPaymentOfferId });
                            }
                          }

                          try {
                            // Notify listeners (e.g., Budget Breakdown page) to refresh debts
                            if (typeof window !== 'undefined') {
                              window.dispatchEvent(new CustomEvent('iehub-debts-changed', { detail: { debtId: savedDebt.id } }));
                            }
                          } catch {
                            // ignore event dispatch failures
                          }
                        }
                      } catch {
                        // If API fails, still update locally
                      }

                      // Update local entry with server id and payment offer id if available
                      const updatedEntry: ArrearFormItem = {
                        ...entry,
                        // If user explicitly cleared repayment, keep local state consistent with API (0)
                        ...(repaymentNumForApi === 0 && repaymentClearedRef.current
                          ? { repaymentAmount: '0' }
                          : {}),
                        ...(typeof savedId === 'number' && savedId > 0 ? { id: savedId } : {}),
                        // Preserve the paymentOfferId if it was set during save
                        ...(typeof (entry as any).paymentOfferId === 'number'
                          ? { paymentOfferId: (entry as any).paymentOfferId }
                          : {}),
                        source: resolvedSource,
                      };

                      if (onChange) {
                        // prevent the auto-open effect from reopening immediately after save
                        suppressNextAutoOpenRef.current = true;
                        let newItems: ArrearFormItem[];
                        if (editingIndex !== null) {
                          newItems = value.map((it, i) => (i === editingIndex ? updatedEntry : it));
                        } else {
                          // Enforce single arrear: replace first if one already exists
                          if (Array.isArray(value) && value.length >= 1) {
                            newItems = value.map((it, i) => (i === 0 ? updatedEntry : it));
                          } else {
                            newItems = [...value, updatedEntry];
                          }
                        }
                        onChange(newItems);
                        // Ensure inArrears remains true after save
                        try {
                          if (form && fieldName) {
                            const fieldPath = String(fieldName || '');
                            if (fieldPath.includes('.')) {
                              const parts = fieldPath.split('.');
                              parts[parts.length - 1] = 'inArrears';
                              const inArrearsPath = parts.join('.');
                              form.setValue(inArrearsPath, true, { shouldValidate: true });
                            }
                          }
                        } catch {
                          // ignore
                        }
                      }
                      wasSavedRef.current = true;
                      repaymentClearedRef.current = false;
                      handleDialogClose();
                    } catch {
                      // Swallow any unexpected error to avoid trapping the dialog
                      wasSavedRef.current = true;
                      repaymentClearedRef.current = false;
                      handleDialogClose();
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  sx={{
                    flex: { xs: 1, md: 'initial' },
                    minWidth: { xs: 'auto', md: '140px' },
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '16px',
                    fontWeight: 500,
                    textTransform: 'none',
                  }}
                  disabled={isSaving || paymentOfferExceedsBudget}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSave();
              }}
            >
              {/* Render form fields */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formFields.map((field: FieldConfig) => {
                  if (renderField) {
                    // Prefix the field name to create a temporary scoped field
                    const scopedField = {
                      ...field,
                      fieldName: `${tempFieldPrefix}.${field.fieldName}`,
                    };
                    return (
                      <Box key={field.fieldName}>
                        {renderField(scopedField)}
                      </Box>
                    );
                  }
                  return null;
                })}
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 3,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    flex: { xs: 1, md: 'initial' },
                    minWidth: { xs: 'auto', md: '140px' },
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: isMobile ? '16px' : '16px',
                    lineHeight: isMobile ? '22px' : '22px',
                    fontWeight: 500,
                    textTransform: 'none',
                  }}
                >
                  Cancell
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    flex: { xs: 1, md: 'initial' },
                    minWidth: { xs: 'auto', md: '140px' },
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: isMobile ? '16px' : '16px',
                    lineHeight: isMobile ? '22px' : '22px',
                    fontWeight: 500,
                    textTransform: 'none',
                  }}
                >

                  {editingIndex !== null ? 'Edit arrears' : formTitle}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            pt: 2,
            px: 3,
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 600, fontSize: isMobile ? '18px' : '20px', lineHeight: isMobile ? '24px' : '28px' }}
          >
            Delete arrears?
          </Typography>
          <IconButton
            onClick={handleCancelDelete}
            size="small"
            sx={{
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pb: 2,
            pt: 0,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '14px' : '16px', lineHeight: isMobile ? '20px' : '22px' }}>
            Are you sure you want to delete this arrears item? This will also set
            "Are you currently in arrears?" to No.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 3,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              type="button"
              variant="text"
              onClick={handleCancelDelete}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                padding: '8px 20px',
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleConfirmDelete}
              sx={{
                backgroundColor: 'error.main',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                padding: '8px 20px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'error.dark',
                },
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  Deleting...
                </Box>
              ) : (
                'Delete'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ArrearForm;
